import { useMemo } from "react";

import type { DeviceType } from "@/components/filters/DeviceTypeFilter";
import { useLatestRequestGuard } from "@/hooks/useLatestRequestGuard";
import type { AnalyticsServiceV2 } from "@/services/features/AnalyticsServiceV2";
import { type DateRange, storePerformanceService } from "@/services/StorePerformanceService";
import type { Store } from "@/types/store";
import { getBrowserTimezone } from "@/utils/storeTime";

type RequestAnalyticsParams = Parameters<AnalyticsServiceV2["getRequestAnalytics"]>[0];

interface UseRequestAnalyticsQueryOptions {
  stores: Store[] | null;
  dateRange?: DateRange;
  deviceType?: DeviceType;
  comparisonDateRange?: DateRange;
  areaIds?: number[];
}

export const useRequestAnalyticsQuery = ({
  stores,
  dateRange,
  deviceType,
  comparisonDateRange,
  areaIds,
}: UseRequestAnalyticsQueryOptions) => {
  const browserTimezone = getBrowserTimezone();
  const beginRequest = useLatestRequestGuard();
  const storeIdsKey = stores?.map((store) => store.id).join(",") ?? "";
  const storeIds = useMemo(
    () => (storeIdsKey ? storeIdsKey.split(",").map(Number) : []),
    [storeIdsKey],
  );
  const areaIdsKey = areaIds?.join(",") ?? "";
  const startDate = storePerformanceService.formatDate(dateRange?.start, browserTimezone);
  const endDate = storePerformanceService.formatDate(dateRange?.end, browserTimezone);
  const comparisonStartDate = storePerformanceService.formatDate(
    comparisonDateRange?.start,
    browserTimezone,
  );
  const comparisonEndDate = storePerformanceService.formatDate(
    comparisonDateRange?.end,
    browserTimezone,
  );
  const hasRequestInputs = Boolean(storeIdsKey && startDate && endDate);
  const hasComparisonRequestInputs = Boolean(comparisonStartDate && comparisonEndDate);
  const requestKey = hasRequestInputs
    ? `${storeIdsKey}:${startDate}:${endDate}:${comparisonStartDate}:${comparisonEndDate}:${deviceType}:${areaIdsKey}`
    : null;
  const baseParams = useMemo<RequestAnalyticsParams | null>(() => {
    if (!hasRequestInputs || !startDate || !endDate) return null;
    return {
      storeIds,
      startDate,
      endDate,
      ...(deviceType && deviceType !== "all" ? { deviceType } : {}),
      ...(areaIdsKey ? { areaIds: areaIdsKey.split(",").map(Number) } : {}),
    };
  }, [areaIdsKey, deviceType, endDate, hasRequestInputs, startDate, storeIds]);

  return {
    beginRequest,
    baseParams,
    comparisonEndDate,
    comparisonStartDate,
    hasComparisonRequestInputs,
    hasRequestInputs,
    requestKey,
    startDate,
    endDate,
  };
};
